import React, { useEffect, useState, useRef, useMemo } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api, formatApiError, API } from "../lib/api";
import {
  LogOut, Plus, Trash2, Edit3, Mail, FileText, Check, Upload, Search,
  Archive, ArchiveRestore, Eye, EyeOff, X, Video as VideoIcon, Star, Instagram, Youtube,
} from "lucide-react";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("news");

  if (user === undefined) return <div className="pt-40 text-center text-neutral-500">Carregando…</div>;
  if (user === null) return <Navigate to="/admin/login" replace />;

  return (
    <div data-testid="page-admin" className="min-h-screen bg-[#FAF9F6] pt-24 md:pt-28 pb-20">
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <div className="flex items-center justify-between mb-10">
          <div>
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#003F88] font-semibold">
              <span className="accent-line" /> Painel interno
            </div>
            <h1 className="font-serif-display text-4xl md:text-5xl mt-3">Olá, {user.name || "equipe"}</h1>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-xs uppercase tracking-widest text-[#003F88]">Ver site</Link>
            <button
              data-testid="admin-logout"
              onClick={logout}
              className="inline-flex items-center gap-2 border border-neutral-300 px-4 py-2 text-xs uppercase tracking-widest hover:bg-neutral-900 hover:text-white hover:border-neutral-900 transition-colors"
            >
              <LogOut size={14} /> Sair
            </button>
          </div>
        </div>

        <div className="flex border-b border-neutral-200 mb-10">
          <TabBtn active={tab === "news"} onClick={() => setTab("news")} icon={<FileText size={14} />} label="Notícias" testid="tab-news" />
          <TabBtn active={tab === "videos"} onClick={() => setTab("videos")} icon={<VideoIcon size={14} />} label="Vídeos" testid="tab-videos" />
          <TabBtn active={tab === "messages"} onClick={() => setTab("messages")} icon={<Mail size={14} />} label="Mensagens" testid="tab-messages" />
        </div>

        {tab === "news" && <NewsManager />}
        {tab === "videos" && <VideosManager />}
        {tab === "messages" && <MessagesManager />}
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, icon, label, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`inline-flex items-center gap-2 px-5 py-3 text-xs uppercase tracking-widest -mb-px border-b-2 ${
        active ? "border-[#003F88] text-[#003F88]" : "border-transparent text-neutral-500"
      }`}
    >
      {icon} {label}
    </button>
  );
}

function NewsManager() {
  const emptyForm = { title: "", excerpt: "", content: "", cover_image: "", category: "Geral", status: "published" };
  const [list, setList] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all/published/draft/archived
  const [search, setSearch] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const formRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/news");
      setList(data);
    } catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let arr = list;
    if (statusFilter !== "all") arr = arr.filter((n) => (n.status || (n.published ? "published" : "draft")) === statusFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      arr = arr.filter((n) => n.title.toLowerCase().includes(q) || (n.category || "").toLowerCase().includes(q));
    }
    return arr;
  }, [list, statusFilter, search]);

  const counts = useMemo(() => {
    const c = { all: list.length, published: 0, draft: 0, archived: 0 };
    list.forEach((n) => {
      const s = n.status || (n.published ? "published" : "draft");
      if (c[s] !== undefined) c[s] += 1;
    });
    return c;
  }, [list]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setSuccess("");
    try {
      const payload = { ...form, published: form.status === "published" };
      if (editingId) {
        await api.put(`/admin/news/${editingId}`, payload);
        setSuccess("Matéria atualizada com sucesso!");
      } else {
        await api.post("/admin/news", payload);
        setSuccess("Matéria criada com sucesso!");
      }
      setForm(emptyForm);
      setEditingId(null);
      load();
    } catch (e2) { setErr(formatApiError(e2)); }
  };

  const changeStatus = async (id, newStatus) => {
    try {
      await api.post(`/admin/news/${id}/status`, { status: newStatus });
      load();
    } catch (e) { setErr(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir definitivamente? Use 'Arquivar' se quiser apenas tirar do ar.")) return;
    await api.delete(`/admin/news/${id}`);
    load();
  };

  const startEdit = (n) => {
    const s = n.status || (n.published ? "published" : "draft");
    setForm({
      title: n.title, excerpt: n.excerpt, content: n.content,
      cover_image: n.cover_image || "", category: n.category || "Geral", status: s,
    });
    setEditingId(n.id);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  };

  const cancelEdit = () => { setEditingId(null); setForm(emptyForm); setErr(""); setSuccess(""); };

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("rosalvo_token");
      const res = await fetch(`${API}/admin/upload`, {
        method: "POST",
        body: fd,
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Erro no upload");
      setForm((f) => ({ ...f, cover_image: data.url }));
    } catch (e) {
      setErr(e.message || formatApiError(e));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-10">
      {/* Form */}
      <form
        ref={formRef}
        onSubmit={submit}
        data-testid="admin-news-form"
        className="bg-white p-6 md:p-8 border border-neutral-200 space-y-4"
      >
        <div className="flex items-center justify-between">
          <div className="font-serif-display text-2xl">
            {editingId ? "Editar matéria" : "Nova matéria"}
          </div>
          {editingId && (
            <button type="button" onClick={cancelEdit} className="text-xs uppercase tracking-widest text-neutral-500 inline-flex items-center gap-1">
              <X size={14} /> Cancelar edição
            </button>
          )}
        </div>
        {err && <div data-testid="admin-news-error" className="text-sm text-red-700 bg-red-50 p-3 border-l-4 border-red-500">{err}</div>}
        {success && <div className="text-sm text-green-700 bg-green-50 p-3 border-l-4 border-green-500">{success}</div>}

        <div className="grid md:grid-cols-2 gap-4">
          <Input label="Título" value={form.title} onChange={(v) => setForm({ ...form, title: v })} required testid="news-title" />
          <Input label="Categoria" value={form.category} onChange={(v) => setForm({ ...form, category: v })} testid="news-category" />
        </div>

        {/* Cover image with upload */}
        <div>
          <label className="text-[11px] uppercase tracking-[0.22em] text-neutral-500">Imagem de capa</label>
          <div className="mt-2 flex flex-col md:flex-row gap-3 items-stretch md:items-center">
            <div className="flex-1">
              <input
                data-testid="news-cover-url"
                value={form.cover_image}
                onChange={(e) => setForm({ ...form, cover_image: e.target.value })}
                placeholder="Cole uma URL ou faça upload →"
                className="w-full bg-transparent border-b border-neutral-300 py-2 focus:border-[#003F88] outline-none"
              />
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => handleUpload(e.target.files?.[0])}
              data-testid="news-cover-file"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              data-testid="news-upload-btn"
              className="inline-flex items-center gap-2 border border-[#003F88] text-[#003F88] px-4 py-2 text-xs uppercase tracking-widest hover:bg-[#003F88] hover:text-white transition-colors disabled:opacity-50"
            >
              <Upload size={14} /> {uploading ? "Enviando..." : "Upload"}
            </button>
          </div>
          {form.cover_image && (
            <div className="mt-3 inline-block border border-neutral-200 p-1">
              <img src={form.cover_image} alt="prévia" className="h-24 object-cover" />
            </div>
          )}
        </div>

        <TextArea label="Resumo (chamada)" value={form.excerpt} onChange={(v) => setForm({ ...form, excerpt: v })} rows={2} required testid="news-excerpt" />
        <TextArea label="Conteúdo completo" value={form.content} onChange={(v) => setForm({ ...form, content: v })} rows={10} required testid="news-content" />

        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3 text-sm">
            <span className="text-[11px] uppercase tracking-[0.22em] text-neutral-500">Status:</span>
            {["published", "draft", "archived"].map((s) => (
              <label key={s} className={`px-3 py-1.5 text-xs uppercase tracking-widest cursor-pointer border ${form.status === s ? "bg-[#003F88] text-white border-[#003F88]" : "border-neutral-300 text-neutral-600"}`}>
                <input
                  type="radio"
                  name="status"
                  value={s}
                  checked={form.status === s}
                  onChange={() => setForm({ ...form, status: s })}
                  className="hidden"
                  data-testid={`news-status-${s}`}
                />
                {s === "published" ? "Publicada" : s === "draft" ? "Rascunho" : "Arquivada"}
              </label>
            ))}
          </div>
          <button
            data-testid="news-submit"
            type="submit"
            className="inline-flex items-center gap-2 bg-[#003F88] text-white px-7 py-3 text-xs uppercase tracking-widest hover:bg-[#002A5C]"
          >
            <Plus size={14} /> {editingId ? "Salvar alterações" : "Criar matéria"}
          </button>
        </div>
      </form>

      {/* Filters */}
      <div className="bg-white border border-neutral-200 p-4 md:p-5">
        <div className="flex flex-wrap items-center gap-2 md:gap-3">
          {[
            { key: "all", label: "Todas" },
            { key: "published", label: "Publicadas" },
            { key: "draft", label: "Rascunhos" },
            { key: "archived", label: "Arquivadas" },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setStatusFilter(f.key)}
              data-testid={`filter-${f.key}`}
              className={`text-xs uppercase tracking-widest px-3 py-2 transition-colors ${
                statusFilter === f.key ? "bg-[#003F88] text-white" : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"
              }`}
            >
              {f.label} <span className="opacity-60 ml-1">({counts[f.key] ?? 0})</span>
            </button>
          ))}
          <div className="flex-1 min-w-[200px] ml-auto relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input
              data-testid="news-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar título ou categoria..."
              className="w-full pl-9 pr-3 py-2 border border-neutral-200 focus:border-[#003F88] outline-none text-sm"
            />
          </div>
        </div>
      </div>

      {/* List */}
      <div className="space-y-3">
        {filtered.length === 0 && (
          <div className="text-center py-12 text-neutral-500 text-sm bg-white border border-dashed border-neutral-300">
            Nenhuma matéria encontrada para este filtro.
          </div>
        )}
        {filtered.map((n) => {
          const s = n.status || (n.published ? "published" : "draft");
          const statusStyle = {
            published: "bg-green-100 text-green-800",
            draft: "bg-amber-100 text-amber-800",
            archived: "bg-neutral-200 text-neutral-600",
          }[s];
          const statusLabel = { published: "Publicada", draft: "Rascunho", archived: "Arquivada" }[s];
          return (
            <div key={n.id} data-testid={`admin-news-row-${n.id}`} className="bg-white border border-neutral-200 p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-4">
              {n.cover_image && (
                <img src={n.cover_image} alt="" className="w-full md:w-24 h-20 object-cover shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 ${statusStyle}`}>{statusLabel}</span>
                  <span className="text-[10px] uppercase tracking-widest text-[#003F88] font-semibold">{n.category}</span>
                </div>
                <div className="font-serif-display text-lg leading-tight">{n.title}</div>
                <div className="text-sm text-neutral-600 mt-1 line-clamp-2">{n.excerpt}</div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <IconBtn title="Editar" onClick={() => startEdit(n)} testid={`news-edit-${n.id}`}><Edit3 size={15} /></IconBtn>
                {s !== "published" && (
                  <IconBtn title="Publicar" onClick={() => changeStatus(n.id, "published")} testid={`news-publish-${n.id}`} className="hover:bg-green-50 hover:text-green-700"><Eye size={15} /></IconBtn>
                )}
                {s === "published" && (
                  <IconBtn title="Tornar rascunho" onClick={() => changeStatus(n.id, "draft")} testid={`news-draft-${n.id}`} className="hover:bg-amber-50 hover:text-amber-700"><EyeOff size={15} /></IconBtn>
                )}
                {s !== "archived" ? (
                  <IconBtn title="Arquivar" onClick={() => changeStatus(n.id, "archived")} testid={`news-archive-${n.id}`} className="hover:bg-neutral-200"><Archive size={15} /></IconBtn>
                ) : (
                  <IconBtn title="Desarquivar (publicar)" onClick={() => changeStatus(n.id, "published")} testid={`news-unarchive-${n.id}`} className="hover:bg-green-50 hover:text-green-700"><ArchiveRestore size={15} /></IconBtn>
                )}
                <IconBtn title="Excluir definitivamente" onClick={() => remove(n.id)} testid={`news-delete-${n.id}`} className="hover:bg-red-50 hover:text-red-600"><Trash2 size={15} /></IconBtn>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function IconBtn({ title, onClick, children, className = "", testid }) {
  return (
    <button data-testid={testid} title={title} onClick={onClick} className={`p-2 text-neutral-600 transition-colors ${className}`}>
      {children}
    </button>
  );
}

function VideosManager() {
  const empty = { title: "", description: "", category: "Campanha", video_url: "", thumbnail: "", featured: false, status: "published" };
  const [list, setList] = useState([]);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const formRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/videos");
      setList(data);
    } catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let arr = list;
    if (statusFilter !== "all") arr = arr.filter((v) => v.status === statusFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      arr = arr.filter((v) => v.title.toLowerCase().includes(q) || (v.category || "").toLowerCase().includes(q));
    }
    return arr;
  }, [list, statusFilter, search]);

  const counts = useMemo(() => {
    const c = { all: list.length, published: 0, draft: 0, archived: 0 };
    list.forEach((v) => { if (c[v.status] !== undefined) c[v.status] += 1; });
    return c;
  }, [list]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setSuccess("");
    try {
      if (editingId) {
        await api.put(`/admin/videos/${editingId}`, form);
        setSuccess("Vídeo atualizado!");
      } else {
        await api.post("/admin/videos", form);
        setSuccess("Vídeo adicionado!");
      }
      setForm(empty);
      setEditingId(null);
      load();
    } catch (e2) { setErr(formatApiError(e2)); }
  };

  const changeStatus = async (id, newStatus) => {
    try {
      await api.post(`/admin/videos/${id}/status`, { status: newStatus });
      load();
    } catch (e) { setErr(formatApiError(e)); }
  };

  const toggleFeatured = async (v) => {
    try {
      await api.put(`/admin/videos/${v.id}`, { ...v, featured: !v.featured });
      load();
    } catch (e) { setErr(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir vídeo definitivamente? Use 'Arquivar' para tirar do ar mantendo no banco.")) return;
    await api.delete(`/admin/videos/${id}`);
    load();
  };

  const startEdit = (v) => {
    setForm({
      title: v.title || "", description: v.description || "", category: v.category || "Campanha",
      video_url: v.video_url || "", thumbnail: v.thumbnail || "", featured: !!v.featured, status: v.status || "published",
    });
    setEditingId(v.id);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  };

  const cancelEdit = () => { setEditingId(null); setForm(empty); setErr(""); setSuccess(""); };

  const provider = useMemo(() => {
    const u = form.video_url || "";
    if (/instagram\.com\/(p|reel|tv)\//.test(u)) return { name: "Instagram", icon: <Instagram size={14} /> };
    if (/(youtube\.com|youtu\.be)/.test(u)) return { name: "YouTube", icon: <Youtube size={14} /> };
    if (/tiktok\.com/.test(u)) return { name: "TikTok", icon: <VideoIcon size={14} /> };
    if (/\.(mp4|webm|mov)/.test(u)) return { name: "Arquivo de vídeo", icon: <VideoIcon size={14} /> };
    if (u.trim()) return { name: "Link genérico", icon: <VideoIcon size={14} /> };
    return null;
  }, [form.video_url]);

  return (
    <div className="space-y-10">
      <form
        ref={formRef}
        onSubmit={submit}
        data-testid="admin-video-form"
        className="bg-white p-6 md:p-8 border border-neutral-200 space-y-4"
      >
        <div className="flex items-center justify-between">
          <div className="font-serif-display text-2xl">
            {editingId ? "Editar vídeo" : "Adicionar vídeo à Vitrine"}
          </div>
          {editingId && (
            <button type="button" onClick={cancelEdit} className="text-xs uppercase tracking-widest text-neutral-500 inline-flex items-center gap-1">
              <X size={14} /> Cancelar
            </button>
          )}
        </div>
        {err && <div className="text-sm text-red-700 bg-red-50 p-3 border-l-4 border-red-500">{err}</div>}
        {success && <div className="text-sm text-green-700 bg-green-50 p-3 border-l-4 border-green-500">{success}</div>}

        <div className="grid md:grid-cols-2 gap-4">
          <Input label="Título" value={form.title} onChange={(v) => setForm({ ...form, title: v })} required testid="video-title" />
          <Input label="Categoria / Pauta" value={form.category} onChange={(v) => setForm({ ...form, category: v })} testid="video-category" />
        </div>

        <div>
          <label className="text-[11px] uppercase tracking-[0.22em] text-neutral-500">URL do vídeo *</label>
          <input
            data-testid="video-url"
            required
            value={form.video_url}
            onChange={(e) => setForm({ ...form, video_url: e.target.value })}
            placeholder="Cole a URL do Instagram (Reel/Post), YouTube, TikTok ou link direto MP4"
            className="mt-2 w-full bg-transparent border-b border-neutral-300 py-2 focus:border-[#003F88] outline-none"
          />
          {provider && (
            <div className="mt-2 inline-flex items-center gap-2 text-xs text-[#003F88] font-medium">
              {provider.icon} Detectado: <strong>{provider.name}</strong>
            </div>
          )}
          <div className="mt-2 text-xs text-neutral-500 leading-relaxed">
            Exemplos: <code className="bg-neutral-100 px-1">instagram.com/reel/Cxxxx/</code> · <code className="bg-neutral-100 px-1">youtu.be/xxxxx</code>
          </div>
        </div>

        <Input label="Thumbnail (opcional — URL de imagem de capa)" value={form.thumbnail} onChange={(v) => setForm({ ...form, thumbnail: v })} testid="video-thumb" />

        <TextArea label="Descrição (opcional)" value={form.description} onChange={(v) => setForm({ ...form, description: v })} rows={3} testid="video-desc" />

        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4 flex-wrap">
            <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={form.featured}
                onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                data-testid="video-featured"
                className="w-4 h-4"
              />
              <Star size={14} className="text-[#FFC72C]" fill="currentColor" />
              <span>Marcar como destaque</span>
            </label>

            <div className="flex items-center gap-2">
              <span className="text-[11px] uppercase tracking-[0.22em] text-neutral-500">Status:</span>
              {["published", "draft", "archived"].map((s) => (
                <label key={s} className={`px-3 py-1.5 text-xs uppercase tracking-widest cursor-pointer border ${form.status === s ? "bg-[#003F88] text-white border-[#003F88]" : "border-neutral-300 text-neutral-600"}`}>
                  <input type="radio" name="vstatus" value={s} checked={form.status === s} onChange={() => setForm({ ...form, status: s })} className="hidden" data-testid={`video-status-${s}`} />
                  {s === "published" ? "Publicado" : s === "draft" ? "Rascunho" : "Arquivado"}
                </label>
              ))}
            </div>
          </div>
          <button
            data-testid="video-submit"
            type="submit"
            className="inline-flex items-center gap-2 bg-[#003F88] text-white px-7 py-3 text-xs uppercase tracking-widest hover:bg-[#002A5C]"
          >
            <Plus size={14} /> {editingId ? "Salvar" : "Adicionar"}
          </button>
        </div>
      </form>

      <div className="bg-white border border-neutral-200 p-4 md:p-5">
        <div className="flex flex-wrap items-center gap-2 md:gap-3">
          {[
            { key: "all", label: "Todos" },
            { key: "published", label: "Publicados" },
            { key: "draft", label: "Rascunhos" },
            { key: "archived", label: "Arquivados" },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setStatusFilter(f.key)}
              data-testid={`vfilter-${f.key}`}
              className={`text-xs uppercase tracking-widest px-3 py-2 transition-colors ${
                statusFilter === f.key ? "bg-[#003F88] text-white" : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"
              }`}
            >
              {f.label} <span className="opacity-60 ml-1">({counts[f.key] ?? 0})</span>
            </button>
          ))}
          <div className="flex-1 min-w-[200px] ml-auto relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input
              data-testid="video-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar vídeo..."
              className="w-full pl-9 pr-3 py-2 border border-neutral-200 focus:border-[#003F88] outline-none text-sm"
            />
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {filtered.length === 0 && (
          <div className="text-center py-12 text-neutral-500 text-sm bg-white border border-dashed border-neutral-300">
            Nenhum vídeo neste filtro. Adicione o primeiro acima.
          </div>
        )}
        {filtered.map((v) => {
          const s = v.status || "published";
          const statusStyle = {
            published: "bg-green-100 text-green-800",
            draft: "bg-amber-100 text-amber-800",
            archived: "bg-neutral-200 text-neutral-600",
          }[s];
          const statusLabel = { published: "Publicado", draft: "Rascunho", archived: "Arquivado" }[s];
          return (
            <div key={v.id} data-testid={`admin-video-row-${v.id}`} className="bg-white border border-neutral-200 p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-4">
              {v.thumbnail ? (
                <img src={v.thumbnail} alt="" className="w-full md:w-20 h-32 md:h-28 object-cover shrink-0 bg-neutral-200" />
              ) : (
                <div className="w-full md:w-20 h-32 md:h-28 shrink-0 bg-[#003F88] flex items-center justify-center">
                  <VideoIcon size={26} className="text-[#FFC72C]" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 ${statusStyle}`}>{statusLabel}</span>
                  {v.featured && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 bg-[#FFC72C] text-[#003F88] font-bold inline-flex items-center gap-1"><Star size={10} fill="currentColor" /> Destaque</span>}
                  <span className="text-[10px] uppercase tracking-widest text-[#003F88] font-semibold">{v.category}</span>
                </div>
                <div className="font-serif-display text-lg leading-tight">{v.title}</div>
                {v.description && <div className="text-sm text-neutral-600 mt-1 line-clamp-2">{v.description}</div>}
                <a href={v.video_url} target="_blank" rel="noreferrer" className="text-xs text-neutral-400 hover:text-[#003F88] truncate block max-w-full mt-1">{v.video_url}</a>
              </div>
              <div className="flex items-center gap-1 shrink-0 flex-wrap">
                <IconBtn title={v.featured ? "Remover destaque" : "Marcar destaque"} onClick={() => toggleFeatured(v)} testid={`video-feature-${v.id}`} className={v.featured ? "text-[#FFC72C]" : ""}>
                  <Star size={15} fill={v.featured ? "currentColor" : "none"} />
                </IconBtn>
                <IconBtn title="Editar" onClick={() => startEdit(v)} testid={`video-edit-${v.id}`}><Edit3 size={15} /></IconBtn>
                {s !== "published" && <IconBtn title="Publicar" onClick={() => changeStatus(v.id, "published")} testid={`video-publish-${v.id}`} className="hover:bg-green-50 hover:text-green-700"><Eye size={15} /></IconBtn>}
                {s === "published" && <IconBtn title="Rascunho" onClick={() => changeStatus(v.id, "draft")} testid={`video-draft-${v.id}`} className="hover:bg-amber-50 hover:text-amber-700"><EyeOff size={15} /></IconBtn>}
                {s !== "archived" ? (
                  <IconBtn title="Arquivar" onClick={() => changeStatus(v.id, "archived")} testid={`video-archive-${v.id}`}><Archive size={15} /></IconBtn>
                ) : (
                  <IconBtn title="Desarquivar" onClick={() => changeStatus(v.id, "published")} testid={`video-unarchive-${v.id}`} className="hover:bg-green-50 hover:text-green-700"><ArchiveRestore size={15} /></IconBtn>
                )}
                <IconBtn title="Excluir definitivamente" onClick={() => remove(v.id)} testid={`video-delete-${v.id}`} className="hover:bg-red-50 hover:text-red-600"><Trash2 size={15} /></IconBtn>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MessagesManager() {
  const [list, setList] = useState([]);
  const load = async () => {
    const { data } = await api.get("/admin/messages");
    setList(data);
  };
  useEffect(() => { load(); }, []);
  const markRead = async (id) => { await api.post(`/admin/messages/${id}/read`); load(); };

  return (
    <div className="space-y-3" data-testid="admin-messages-list">
      {list.length === 0 && <div className="text-neutral-500 text-sm">Nenhuma mensagem recebida ainda.</div>}
      {list.map((m) => (
        <div key={m.id} data-testid={`admin-msg-${m.id}`} className={`bg-white border border-neutral-200 p-6 ${!m.read ? "border-l-4 border-l-[#FFC72C]" : ""}`}>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#003F88]">{m.subject} · {m.neighborhood || "—"}</div>
              <div className="font-serif-display text-lg mt-1">{m.name} <span className="text-neutral-400 text-base">· {m.email}</span></div>
              <div className="text-xs text-neutral-500">{m.phone || "sem telefone"} · {new Date(m.created_at).toLocaleString("pt-BR")}</div>
            </div>
            {!m.read && (
              <button onClick={() => markRead(m.id)} className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#003F88]" data-testid={`msg-read-${m.id}`}>
                <Check size={14} /> Marcar como lida
              </button>
            )}
          </div>
          <p className="mt-4 text-neutral-700 whitespace-pre-line">{m.message}</p>
        </div>
      ))}
    </div>
  );
}

function Input({ label, value, onChange, required, testid }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-[0.22em] text-neutral-500">{label}{required ? " *" : ""}</span>
      <input data-testid={testid} required={required} value={value} onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full bg-transparent border-b border-neutral-300 py-2 focus:border-[#003F88] outline-none" />
    </label>
  );
}

function TextArea({ label, value, onChange, rows = 3, required, testid }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-[0.22em] text-neutral-500">{label}{required ? " *" : ""}</span>
      <textarea data-testid={testid} required={required} rows={rows} value={value} onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full bg-transparent border border-neutral-300 p-3 focus:border-[#003F88] outline-none resize-none font-body" />
    </label>
  );
}
